/**
 * useStripeCheckout Hook
 * 
 * Manages Stripe Checkout flow
 */

import { useState, useCallback } from 'react';
import {
  createAndRedirectToCheckout,
  verifyPayment,
  isStripeTestMode,
} from '@/services/payment/stripeService';
import type { TransactionModel, TransactionResponse } from '@/types';

export interface UseStripeCheckoutResult {
  loading: boolean;
  error: Error | null;
  isTestMode: boolean;
  createSession: (request: TransactionModel) => Promise<void>;
  verify: (sessionId: string, companyId: number) => Promise<TransactionResponse>;
}

/**
 * Hook to manage Stripe Checkout flow
 */
export function useStripeCheckout(): UseStripeCheckoutResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Create checkout session and redirect to Stripe
   */
  const createSession = useCallback(async (request: TransactionModel) => {
    setLoading(true);
    setError(null);

    try {
      await createAndRedirectToCheckout(request);
      // If successful, user will be redirected to Stripe
      // Loading will stay true until redirect completes
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create checkout session');
      setError(error);
      setLoading(false);
      throw error;
    }
  }, []);

  /**
   * Verify payment after returning from Stripe
   */
  const verify = useCallback(async (sessionId: string, companyId: number): Promise<TransactionResponse> => {
    setLoading(true);
    setError(null);

    try {
      const result = await verifyPayment(sessionId, companyId);
      setLoading(false);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to verify payment');
      setError(error);
      setLoading(false);
      throw error;
    }
  }, []);

  return {
    loading,
    error,
    isTestMode: isStripeTestMode(),
    createSession,
    verify,
  };
}
